import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AmazonReports.module.css';
import AuthContext from '../../context/AuthContext';
import useSingleFlight from '../../hooks/useSingleFlight';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// createReport is throttled to roughly one call per minute, so there is nothing
// to gain from polling faster than this.
const POLL_INTERVAL_MS = 5000;
const DEFAULT_RANGE_DAYS = 30;

const AVAILABILITY_FILTERS = [
    { value: '', label: 'All reports' },
    { value: 'seller', label: 'Seller only' },
    { value: 'both', label: 'Seller & Vendor' },
    { value: 'vendor', label: 'Vendor only' },
];

const AVAILABILITY_LABELS = {
    seller: 'Seller only',
    both: 'Seller & Vendor',
    vendor: 'Vendor only',
};

const toDateInput = (date) => date.toISOString().slice(0, 10);
const addDays = (date, days) => new Date(date.getTime() + days * 86400000);

// Brand Analytics only accepts a range landing exactly on the period's
// boundaries, and rejects a misaligned one as FATAL *after* queueing the report.
// These mirror the backend's rules so the defaults are always valid.
const lastCompletePeriod = (period, today = new Date()) => {
    const utc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    if (period === 'WEEK') {
        // Amazon's weeks run Sunday–Saturday.
        const end = addDays(utc, -utc.getUTCDay() - 1);
        return { start_date: toDateInput(addDays(end, -6)), end_date: toDateInput(end) };
    }
    if (period === 'MONTH') {
        const end = new Date(Date.UTC(utc.getUTCFullYear(), utc.getUTCMonth(), 0));
        return {
            start_date: toDateInput(new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1))),
            end_date: toDateInput(end),
        };
    }
    if (period === 'QUARTER') {
        const startMonth = Math.floor(utc.getUTCMonth() / 3) * 3;
        const end = new Date(Date.UTC(utc.getUTCFullYear(), startMonth, 0));
        return {
            start_date: toDateInput(new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - 2, 1))),
            end_date: toDateInput(end),
        };
    }
    // DAY (and anything unknown): the last fully elapsed day.
    const day = toDateInput(addDays(utc, -1));
    return { start_date: day, end_date: day };
};

// Defaults the window to the largest span the report allows, capped at 30 days
// so a first click never trips Amazon's per-request limit. Period-aligned
// reports instead default to the last complete period.
const defaultRange = (spec) => {
    if (spec.period_aligned) {
        const periods = spec.options?.reportPeriod || [];
        return lastCompletePeriod(periods.includes('WEEK') ? 'WEEK' : periods[0]);
    }
    const span = Math.min(spec.max_range_days || DEFAULT_RANGE_DAYS, DEFAULT_RANGE_DAYS);
    const end = new Date();
    const start = new Date(end.getTime() - (span - 1) * 86400000);
    return { start_date: toDateInput(start), end_date: toDateInput(end) };
};

// Writes the payload straight to the user's disk. Revoking the object URL is
// what stops each download from pinning its blob in memory for the session.
const downloadJson = (payload, filename) => {
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const reportFilename = (reportType, suffix = '') =>
    `${reportType}${suffix}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;

const request = async (token, path, { method = 'GET', body } = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`);
    }
    return { data, status: response.status };
};

const Badge = ({ tone = 'neutral', children }) => (
    <span className={`${styles.badge} ${styles[tone]}`}>{children}</span>
);

const Spinner = () => <span className={styles.spinner} aria-hidden="true" />;

// Names the phase the request is actually in — "Working…" on both a 1-second
// create and a 4-minute poll reads as a stuck button.
const busyLabel = (status, idle) => {
    if (status === 'requesting') return 'Requesting…';
    if (status === 'polling') return 'Processing…';
    return idle;
};

// Background sync (runs automatically after Amazon connect). Reads the state
// the amazon-reports-sync worker persists; polls only while it's PROCESSING.
const SYNC_POLL_INTERVAL_MS = 30000;

const SYNC_STATUS_TONE = {
    PROCESSING: 'info',
    COMPLETED: 'good',
    PARTIAL: 'warn',
    FAILED: 'bad',
    PENDING: 'neutral',
    SKIPPED: 'neutral',
};

const SyncPanel = ({ sync }) => {
    const [expanded, setExpanded] = useState(false);
    if (!sync) return null;

    const { done = 0, total = 0 } = sync.progress || {};
    const pct = total ? Math.round((done / total) * 100) : 0;
    const chips = Object.entries(sync.counts || {}).filter(([, n]) => n > 0);

    return (
        <section className={styles.syncPanel}>
            <div className={styles.syncHeader}>
                <div className={styles.syncTitleRow}>
                    <h2 className={styles.syncTitle}>Background sync</h2>
                    <Badge tone={SYNC_STATUS_TONE[sync.status] || 'neutral'}>{sync.status}</Badge>
                    {sync.status === 'PROCESSING' && <Spinner />}
                </div>
                <span className={styles.syncProgressText}>{done}/{total} reports</span>
            </div>
            <div className={styles.syncBar}>
                <div className={styles.syncBarFill} style={{ width: `${pct}%` }} />
            </div>
            <div className={styles.syncChipsRow}>
                <div className={styles.syncChips}>
                    {chips.map(([status, n]) => (
                        <Badge key={status} tone={SYNC_STATUS_TONE[status] || 'neutral'}>
                            {n} {status.toLowerCase()}
                        </Badge>
                    ))}
                </div>
                <button className={styles.syncToggle} onClick={() => setExpanded((v) => !v)}>
                    {expanded ? 'Hide details' : 'Show details'}
                </button>
            </div>
            {sync.error && <p className={styles.error}>{sync.error}</p>}
            {expanded && (
                <table className={styles.syncTable}>
                    <tbody>
                        {(sync.reports || []).map((r) => (
                            <tr key={r.report_type}>
                                <td className={styles.syncTableName}>{r.name}</td>
                                <td><Badge tone={SYNC_STATUS_TONE[r.status] || 'neutral'}>{r.status}</Badge></td>
                                <td className={styles.syncTableDetail}>
                                    {r.status === 'COMPLETED' && r.rows != null ? `${r.rows} rows` : (r.error || '')}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
};

const ReportCard = ({ spec, state, form, onFormChange, onFetch, onRequestOnly, onListExisting }) => {
    const needsDates = spec.date_range === 'required';
    const busy = state.status === 'requesting' || state.status === 'polling';
    const optionNames = Object.keys(spec.options || {});

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <div>
                    <h3 className={styles.cardTitle}>{spec.name}</h3>
                    <code className={styles.reportType}>{spec.report_type}</code>
                </div>
                <div className={styles.badges}>
                    <Badge tone={spec.availability === 'both' ? 'info'
                        : spec.availability === 'vendor' ? 'warn' : 'neutral'}>
                        {AVAILABILITY_LABELS[spec.availability] || spec.availability}
                    </Badge>
                    <Badge>{spec.format.toUpperCase()}</Badge>
                    {spec.brand_registry && <Badge tone="warn">Brand Registry</Badge>}
                    {!spec.on_demand && <Badge tone="warn">Scheduled only</Badge>}
                    {!needsDates && spec.on_demand && <Badge>Snapshot</Badge>}
                </div>
            </div>

            {spec.notes && <p className={styles.notes}>{spec.notes}</p>}

            {(needsDates || optionNames.length > 0) && spec.on_demand && (
                <div className={styles.controls}>
                    {needsDates && (
                        <>
                            <label className={styles.field}>
                                <span>From</span>
                                <input
                                    type="date"
                                    value={form.start_date || ''}
                                    onChange={(e) => onFormChange('start_date', e.target.value)}
                                />
                            </label>
                            <label className={styles.field}>
                                <span>To</span>
                                <input
                                    type="date"
                                    value={form.end_date || ''}
                                    onChange={(e) => onFormChange('end_date', e.target.value)}
                                />
                            </label>
                        </>
                    )}
                    {optionNames.map((name) => {
                        const allowed = spec.options[name];
                        return (
                            <label key={name} className={styles.field}>
                                <span>{name}</span>
                                {allowed.length > 0 ? (
                                    <select
                                        value={form[name] || ''}
                                        onChange={(e) => onFormChange(name, e.target.value)}
                                    >
                                        <option value="">—</option>
                                        {allowed.map((value) => (
                                            <option key={value} value={value}>{value}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        value={form[name] || ''}
                                        placeholder="optional"
                                        onChange={(e) => onFormChange(name, e.target.value)}
                                    />
                                )}
                            </label>
                        );
                    })}
                </div>
            )}

            {needsDates && spec.max_range_days && (
                <p className={styles.hint}>Amazon accepts at most {spec.max_range_days} days per request.</p>
            )}
            {spec.period_aligned && (
                <p className={styles.hint}>
                    Range must land exactly on {String(form.reportPeriod || 'the period').toLowerCase()}
                    {' '}boundaries{form.reportPeriod === 'WEEK' ? ' (Sunday–Saturday)' : ''} — changing
                    the period re-snaps these dates to the last complete one.
                </p>
            )}
            {spec.availability === 'vendor' && (
                <p className={styles.hint}>
                    Needs a Vendor Central authorisation — a seller-connected account will get an
                    error from Amazon.
                </p>
            )}

            <div className={styles.actions}>
                {spec.on_demand ? (
                    <>
                        <button
                            className={`${styles.primaryButton} ${busy ? styles.busy : ''}`}
                            onClick={onFetch}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {busy && <Spinner />}
                            {busyLabel(state.status, 'Fetch & download JSON')}
                        </button>
                        <button className={styles.secondaryButton} onClick={onRequestOnly} disabled={busy}>
                            Request only
                        </button>
                    </>
                ) : (
                    <button
                        className={`${styles.primaryButton} ${busy ? styles.busy : ''}`}
                        onClick={onListExisting}
                        disabled={busy}
                        aria-busy={busy}
                    >
                        {busy && <Spinner />}
                        {busyLabel(state.status, 'List existing & download JSON')}
                    </button>
                )}
            </div>

            {state.message && (
                <p
                    className={state.status === 'error' ? styles.error
                        : state.status === 'done' ? styles.success : styles.status}
                    role="status"
                >
                    {state.message}
                </p>
            )}
            {state.errorDetail && (
                <pre className={styles.errorDetail}>
                    {typeof state.errorDetail === 'string'
                        ? state.errorDetail
                        : JSON.stringify(state.errorDetail, null, 2)}
                </pre>
            )}
            {state.reportId && (
                <p className={styles.status}>
                    Report ID: <code className={styles.reportType}>{state.reportId}</code>
                </p>
            )}
        </div>
    );
};

const AmazonReports = () => {
    const { token } = useContext(AuthContext);
    const [specs, setSpecs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [availability, setAvailability] = useState('');
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');
    const [forms, setForms] = useState({});
    const [states, setStates] = useState({});
    const [syncs, setSyncs] = useState([]);
    // Polls outlive a single handler, so they are tracked here and cleared on
    // unmount — otherwise a navigation away leaves timers running forever.
    const timersRef = useRef({});
    const mountedRef = useRef(true);

    // The flag has to be re-armed in the effect body, not just cleared in the
    // cleanup: StrictMode mounts, unmounts and remounts in development, so a
    // cleanup-only guard latches to false on the first pass and every later
    // setState is silently dropped.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            Object.values(timersRef.current).forEach(clearTimeout);
            timersRef.current = {};
        };
    }, []);

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const { data } = await request(token, '/amazon/reports/types');
                setSpecs(data.reports || []);
                setForms(Object.fromEntries((data.reports || []).map((spec) => [
                    spec.report_type,
                    {
                        ...(spec.date_range === 'required' ? defaultRange(spec) : {}),
                        // Pre-select the period the default range was built for,
                        // so the two can never disagree on first render.
                        ...(spec.period_aligned
                            ? { reportPeriod: (spec.options?.reportPeriod || []).includes('WEEK')
                                ? 'WEEK' : (spec.options?.reportPeriod || [])[0] }
                            : {}),
                    },
                ])));
            } catch (err) {
                setLoadError(err.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    // Background-sync progress. Refreshes on an interval only while a sync is
    // PROCESSING — a finished sync's status never changes on its own.
    useEffect(() => {
        if (!token) return undefined;
        let timer = null;
        const load = async () => {
            try {
                const { data } = await request(token, '/amazon/reports/sync-status');
                if (!mountedRef.current) return;
                const list = data.syncs || [];
                setSyncs(list);
                if (list.some((s) => s.status === 'PROCESSING')) {
                    timer = setTimeout(load, SYNC_POLL_INTERVAL_MS);
                }
            } catch (err) {
                // Non-fatal: the manual report cards work without sync status.
            }
        };
        load();
        return () => clearTimeout(timer);
    }, [token]);

    const setState = useCallback((reportType, patch) => {
        if (!mountedRef.current) return;
        setStates((prev) => ({ ...prev, [reportType]: { ...prev[reportType], ...patch } }));
    }, []);

    // Changing the period re-snaps the dates: a range valid for WEEK is almost
    // never valid for MONTH, and Amazon only reports that as a FATAL later.
    const handleFormChange = useCallback((spec, key, value) => {
        setForms((prev) => {
            const next = { ...prev[spec.report_type], [key]: value };
            if (key === 'reportPeriod' && spec.period_aligned && value) {
                Object.assign(next, lastCompletePeriod(value));
            }
            return { ...prev, [spec.report_type]: next };
        });
    }, []);

    // Splits the form back into the date range and the reportOptions Amazon wants,
    // dropping blanks so an untouched optional field is never sent as "".
    const buildBody = useCallback((spec) => {
        const form = forms[spec.report_type] || {};
        const body = { report_type: spec.report_type };
        if (spec.date_range === 'required') {
            body.start_date = form.start_date;
            body.end_date = form.end_date;
        }
        const options = Object.entries(spec.options || {})
            .filter(([name]) => form[name])
            .reduce((acc, [name]) => ({ ...acc, [name]: form[name] }), {});
        if (Object.keys(options).length) body.report_options = options;
        return body;
    }, [forms]);

    const downloadDocument = useCallback(async (reportType, reportId) => {
        const { data } = await request(token, `/amazon/reports/${reportId}/document`);
        downloadJson(data, reportFilename(reportType));
        setState(reportType, {
            status: 'done',
            message: `Downloaded${data.row_count != null ? ` ${data.row_count} rows` : ''}${data.truncated ? ' (truncated — raise max_rows to get the rest)' : ''}.`,
        });
    }, [token, setState]);

    // Re-arms itself until Amazon reports a terminal status, then downloads.
    const poll = useCallback((reportType, reportId) => {
        timersRef.current[reportType] = setTimeout(async () => {
            if (!mountedRef.current) return;
            try {
                const { data } = await request(token, `/amazon/reports/${reportId}`);
                if (data.ready) {
                    await downloadDocument(reportType, reportId);
                } else if (['CANCELLED', 'FATAL'].includes(data.processing_status)) {
                    setState(reportType, {
                        status: 'error',
                        message: `Amazon returned ${data.processing_status}.`,
                        errorDetail: data.error_detail,
                    });
                } else {
                    setState(reportType, {
                        status: 'polling',
                        message: `Processing (${data.processing_status})…`,
                    });
                    poll(reportType, reportId);
                }
            } catch (err) {
                setState(reportType, { status: 'error', message: err.message });
            }
        }, POLL_INTERVAL_MS);
    }, [token, downloadDocument, setState]);

    // Guarded because createReport is throttled to about one call per minute —
    // firing several at once just earns 429s.
    const handleFetch = useSingleFlight(async (spec) => {
        setState(spec.report_type, { status: 'requesting', message: 'Requesting…', reportId: null, errorDetail: null });
        try {
            const { data, status } = await request(token, '/amazon/reports/fetch', {
                method: 'POST', body: buildBody(spec),
            });
            if (status === 200 && data.ready) {
                downloadJson(data, reportFilename(spec.report_type));
                setState(spec.report_type, {
                    status: 'done',
                    reportId: data.report_id,
                    message: `Downloaded${data.row_count != null ? ` ${data.row_count} rows` : ''}${data.truncated ? ' (truncated)' : ''}.`,
                });
                return;
            }
            // A terminal-but-not-DONE status must not be polled — it will never
            // change. Amazon's reason travels with it.
            if (['CANCELLED', 'FATAL'].includes(data.processing_status)) {
                setState(spec.report_type, {
                    status: 'error',
                    reportId: data.report_id,
                    message: data.message,
                    errorDetail: data.error_detail,
                });
                return;
            }
            setState(spec.report_type, {
                status: 'polling',
                reportId: data.report_id,
                message: data.message || 'Still processing — polling…',
            });
            if (data.report_id) poll(spec.report_type, data.report_id);
        } catch (err) {
            setState(spec.report_type, { status: 'error', message: err.message });
        }
    });

    const handleRequestOnly = useSingleFlight(async (spec) => {
        setState(spec.report_type, { status: 'requesting', message: 'Requesting…', reportId: null, errorDetail: null });
        try {
            const { data } = await request(token, '/amazon/reports/request', {
                method: 'POST', body: buildBody(spec),
            });
            setState(spec.report_type, {
                status: 'polling',
                reportId: data.report_id,
                message: 'Queued with Amazon — polling until ready, then downloading.',
            });
            poll(spec.report_type, data.report_id);
        } catch (err) {
            setState(spec.report_type, { status: 'error', message: err.message });
        }
    });

    // Scheduled-only reports (settlement) cannot be requested — this lists what
    // Amazon has already generated and downloads the newest finished one.
    const handleListExisting = useSingleFlight(async (spec) => {
        setState(spec.report_type, { status: 'requesting', message: 'Listing…', reportId: null, errorDetail: null });
        try {
            const { data } = await request(
                token, `/amazon/reports?report_type=${encodeURIComponent(spec.report_type)}&page_size=20`);
            const reports = data.reports || [];
            downloadJson(data, reportFilename(spec.report_type, '_list'));
            const ready = reports.find((r) => r.processingStatus === 'DONE');
            if (!ready) {
                setState(spec.report_type, {
                    status: 'done',
                    message: `Downloaded the list — ${reports.length} report(s), none finished yet.`,
                });
                return;
            }
            setState(spec.report_type, { status: 'polling', reportId: ready.reportId, message: 'Downloading latest…' });
            await downloadDocument(spec.report_type, ready.reportId);
        } catch (err) {
            setState(spec.report_type, { status: 'error', message: err.message });
        }
    });

    const categories = useMemo(
        () => [...new Set(specs.map((s) => s.category))].sort(), [specs]);

    const visible = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return specs.filter((spec) => (
            (!availability || spec.availability === availability) &&
            (!category || spec.category === category) &&
            (!needle ||
                spec.name.toLowerCase().includes(needle) ||
                spec.report_type.toLowerCase().includes(needle) ||
                spec.features.some((f) => f.toLowerCase().includes(needle)))
        ));
    }, [specs, availability, category, search]);

    if (loading) {
        return <div className={styles.container}><p className={styles.status}>Loading report catalogue…</p></div>;
    }

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.title}>Amazon Reports</h1>
                <p className={styles.subtitle}>
                    Seller-accessible SP-API reports. Results download as JSON automatically once Amazon finishes.
                </p>
            </header>

            {loadError && <p className={styles.error}>{loadError}</p>}

            {syncs.map((sync) => (
                <SyncPanel key={sync.seller_id || 'sync'} sync={sync} />
            ))}

            <div className={styles.filters}>
                <select value={availability} onChange={(e) => setAvailability(e.target.value)}>
                    {AVAILABILITY_FILTERS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                </select>
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input
                    type="search"
                    placeholder="Search report or feature…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <span className={styles.count}>{visible.length} of {specs.length}</span>
            </div>

            <div className={styles.grid}>
                {visible.map((spec) => (
                    <ReportCard
                        key={spec.report_type}
                        spec={spec}
                        state={states[spec.report_type] || {}}
                        form={forms[spec.report_type] || {}}
                        onFormChange={(key, value) => handleFormChange(spec, key, value)}
                        onFetch={() => handleFetch(spec)}
                        onRequestOnly={() => handleRequestOnly(spec)}
                        onListExisting={() => handleListExisting(spec)}
                    />
                ))}
            </div>

            {!visible.length && <p className={styles.status}>No reports match these filters.</p>}
        </div>
    );
};

export default AmazonReports;
